import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import useDebouncedValue from '../useDebouncedValue';
import { searchGuests } from '../../services/customers';
import useUser from '../global/useUser';

interface Options {
  enabled?: boolean;
  employeeIds?: string[];
  allowEmptyQuery?: boolean;
}

function useSearchGuests(query: string, options?: Options) {
  const enabled = options?.enabled ?? true;
  const employeeIds = options?.employeeIds;
  const allowEmptyQuery = options?.allowEmptyQuery ?? false;

  const debouncedQuery = useDebouncedValue(query, 250);
  const { user } = useUser();
  const businessId = user?.business?.id;

  const trimmedQuery = debouncedQuery?.trim() ?? '';

  // Sort so the same artists picked in a different order reuse the same cache entry
  const sortedEmployeeIds = React.useMemo(
    () => (employeeIds?.length ? [...employeeIds].sort() : undefined),
    [employeeIds],
  );

  const { data, isLoading, isFetching, isError, error } = useQuery({
    queryKey: [
      'search-guests',
      trimmedQuery,
      businessId,
      sortedEmployeeIds,
      allowEmptyQuery,
    ],
    queryFn: async () => {
      if (!trimmedQuery && !allowEmptyQuery) return [];
      return searchGuests({
        query: trimmedQuery,
        businessId: businessId!,
        employeeIds: sortedEmployeeIds,
      });
    },
    enabled: !!businessId && enabled && (allowEmptyQuery || !!trimmedQuery),
  });

  return {
    data,
    isLoading,
    isFetching,
    isError,
    error,
  };
}

export default useSearchGuests;
